import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// --- Initial Mock Data ---
const initialCourses = [
  { id: 1, title: 'Annual Security & Compliance (2025)', progress: 0, due: 'Jan 31', hours: 1, type: 'Mandatory', category: 'Compliance', status: 'To Start' },
  { id: 2, title: 'Workplace Harassment Prevention', progress: 100, due: 'Completed', hours: 2, type: 'Mandatory', category: 'Compliance', status: 'Completed' },
  { id: 3, title: 'Advanced React State Management (Q3)', progress: 75, due: 'None', hours: 8, type: 'Elective', category: 'Technical', status: 'In Progress' },
  { id: 4, title: 'Leadership & Conflict Resolution', progress: 40, due: '3 Weeks', hours: 5, type: 'Recommended', category: 'Soft Skills', status: 'In Progress' },
  { id: 5, title: 'Q1 Financial Reporting Essentials', progress: 100, due: 'Completed', hours: 4, type: 'Mandatory', category: 'Finance', status: 'Completed' },
  { id: 6, title: 'Effective Remote Team Communication', progress: 0, due: 'None', hours: 3, type: 'Elective', category: 'Soft Skills', status: 'To Start' },
  { id: 7, title: 'Product Launch Strategy Masterclass', progress: 95, due: '2 Days', hours: 6, type: 'Recommended', category: 'Product', status: 'In Progress' },
];

const goalsLinked = [
    { id: 101, name: 'Achieve Q3 Revenue Target', link: 'Product Launch Strategy Masterclass', progress: 95 },
    { id: 102, name: 'Improve Conflict Resolution Score', link: 'Leadership & Conflict Resolution', progress: 40 },
];

const userData = {
  name: 'Alex Johnson',
  role: 'Product Manager',
  department: 'R&D',
  badgesEarned: 7,
};

// --- Utility Components ---

// Simple Notification/Alert Box
const Notification = ({ message, onClose }) => {
    return (
        <motion.div
            className="fixed bottom-6 right-6 p-4 bg-indigo-600 text-white rounded-lg shadow-xl z-50 flex items-center gap-4"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3 }}
        >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-semibold font-[Poppins]">{message}</span>
            <button onClick={onClose} className="text-white/80 hover:text-white transition">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </motion.div>
    );
};


// Simplified Progress Bar for Course Cards
const MiniProgressBar = ({ progress }) => (
    <div className="w-full bg-slate-200 rounded-full h-1.5 overflow-hidden">
        <motion.div
            className={`h-1.5 rounded-full ${progress === 100 ? 'bg-emerald-500' : 'bg-indigo-500'}`}
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8 }}
        />
    </div>
);

// --- NEW: Enterprise Progress Breakdown Component ---
const ProgressStackBreakdown = ({ stats }) => {
    const total = stats.totalEnrolled;
    if (total === 0) return (
        <p className="text-center text-slate-500 mt-4">No courses enrolled to calculate progress.</p>
    );

    const mandatoryCompletedPercent = Math.round((stats.mandatoryCompletedCount / total) * 100);
    const electiveCompletedPercent = Math.round((stats.electiveCompletedCount / total) * 100);
    const inProgressPercent = Math.round((stats.inProgressCount / total) * 100);
    const toStartPercent = 100 - mandatoryCompletedPercent - electiveCompletedPercent - inProgressPercent;

    const data = [
        { label: 'Mandatory Completed', count: stats.mandatoryCompletedCount, percent: mandatoryCompletedPercent, color: 'bg-emerald-600', icon: 'M5 13l4 4L19 7' },
        { label: 'Elective Completed', count: stats.electiveCompletedCount, percent: electiveCompletedPercent, color: 'bg-indigo-600', icon: 'M5 13l4 4L19 7' },
        { label: 'In Progress', count: stats.inProgressCount, percent: inProgressPercent, color: 'bg-amber-500', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
        { label: 'To Start', count: stats.toStartCount, percent: toStartPercent, color: 'bg-slate-300', icon: 'M12 12h.01M16 12h.01M20 12h.01M4 12h.01M16 16h.01M16 8h.01M8 16h.01M8 8h.01' },
    ].filter(d => d.percent > 0);

    return (
        <div className="space-y-4">
            {/* Stacked Bar Visualization */}
            <div className="w-full h-3 rounded-full overflow-hidden flex shadow-inner">
                {data.map((item, index) => (
                    <motion.div
                        key={item.label}
                        className={`${item.color} h-full`}
                        style={{ width: `${item.percent}%` }}
                        initial={{ opacity: 0, scaleX: 0 }}
                        animate={{ opacity: 1, scaleX: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                    />
                ))}
            </div>

            {/* Detailed Legend/Breakdown */}
            <div className="grid grid-cols-2 gap-3 pt-2">
                {data.map(item => (
                    <div key={item.label} className="flex items-center space-x-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${item.color.replace('bg-', 'text-')}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                        </svg>
                        <div className="flex-1">
                            <p className="text-xs font-semibold text-slate-700 font-[Poppins]">{item.label}</p>
                            <p className="text-sm font-bold text-slate-900 font-[Roboto]">{item.count} Courses ({item.percent}%)</p>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="text-center pt-4 border-t border-slate-100">
                <p className="text-3xl font-extrabold text-slate-800 font-[Roboto]">{stats.overallProgress}%</p>
                <p className="text-sm text-slate-600 font-[Poppins] mt-1">Overall Learning Completion</p>
            </div>
        </div>
    );
};
// --- Course Card V4 (Actionable) ---
const CourseItemCard = ({ course, onAction }) => {
    const isCompleted = course.progress === 100;
    const isMandatory = course.type === 'Mandatory';
    const isOverdue = course.due.includes('Day') && course.progress < 100 && parseInt(course.due) < 5;

    let statusStyle = 'text-slate-500 bg-slate-100';
    let buttonText = 'Start';
    let buttonStyle = 'bg-indigo-600 hover:bg-indigo-700';

    if (isCompleted) {
        statusStyle = 'text-emerald-700 bg-emerald-100';
        buttonText = 'Review';
        buttonStyle = 'bg-emerald-600 hover:bg-emerald-700';
    } else if (isMandatory) {
        statusStyle = 'text-red-700 bg-red-100';
        buttonText = course.progress > 0 ? 'Resume' : 'Start';
        buttonStyle = 'bg-rose-600 hover:bg-rose-700';
    } else if (course.progress > 0) {
        buttonText = 'Resume';
    }

    // Determine the action when the button is clicked
    const handleClick = () => {
        if (isCompleted) {
            onAction(course.id, 'review');
        } else if (course.progress < 100 && course.progress > 0) {
            onAction(course.id, 'resume');
        } else if (course.progress === 0 || course.progress < 50) {
             onAction(course.id, 'start');
        } else {
             onAction(course.id, 'complete'); // Default to completion for high-progress courses in demo
        }
    };

    return (
        <motion.div
            className="bg-white p-4 rounded-xl shadow-md border border-slate-200 hover:border-indigo-400 transition duration-300 flex flex-col space-y-3"
            layout
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
        >
            <div className="flex justify-between items-start">
                <div className="flex-1 min-w-0 pr-4">
                    <p className="text-sm font-medium text-slate-500 font-[Poppins] uppercase tracking-wider">
                        {course.category}
                    </p>
                    <h4 className="text-lg font-bold text-slate-900 truncate font-[Roboto]" title={course.title}>
                        {course.title}
                    </h4>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-medium flex-shrink-0 ${statusStyle} font-[Poppins]`}>
                    {isCompleted ? 'Completed' : course.type}
                </span>
            </div>

            <div className="flex items-center justify-between text-sm font-[Poppins] border-t pt-3">
                <div className="flex items-center space-x-4">
                    <span className="text-slate-600 flex items-center gap-1">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        {course.hours} hrs
                    </span>
                    <span className={`flex items-center gap-1 ${isOverdue ? 'text-rose-500 font-semibold' : 'text-slate-600'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h.01M8 21h8a2 2 0 002-2V7a2 2 0 00-2-2H8a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Due: {course.due}
                    </span>
                </div>

                <button
                    className={`px-4 py-1.5 text-sm rounded-lg font-bold text-white transition shadow-md ${buttonStyle} font-[Roboto]`}
                    onClick={handleClick}
                >
                    {buttonText}
                </button>
            </div>
            
            {/* Progress Visualization */}
            <div className="pt-2">
                <p className="text-xs text-slate-500 mb-1">Progress: {course.progress}%</p>
                <MiniProgressBar progress={course.progress} />
            </div>

        </motion.div>
    );
};

// --- Main Dashboard Component ---
export default function LMSDashboardV4() {
    // State to hold the courses and their current progress
    const [courses, setCourses] = useState(initialCourses);
    const [filterType, setFilterType] = useState('inProgress'); // Default to In Progress for actionability
    const [searchTerm, setSearchTerm] = useState('');
    const [notification, setNotification] = useState(null);

    // ACTION HANDLER: Updates the course progress in state
    const handleCourseAction = (courseId, action) => {
        const course = courses.find(c => c.id === courseId);
        if (!course) return;

        let message = '';
        let newCourses = courses;

        if (action === 'complete' || action === 'start') {
            newCourses = courses.map(c =>
                c.id === courseId ? { ...c, progress: 100, status: 'Completed', due: 'Completed' } : c
            );
            message = `Course completed: ${course.title} marked as 100%!`;
        } else if (action === 'resume') {
             newCourses = courses.map(c =>
                c.id === courseId ? { ...c, progress: c.progress + 10, status: 'In Progress' } : c
            );
             message = `Resuming: ${course.title}. Progress increased to ${Math.min(course.progress + 10, 100)}%.`;
        } else if (action === 'review') {
             message = `Navigating to review page for: ${course.title}.`;
        }
        
        setCourses(newCourses);
        setNotification(message);
        
        // Clear notification after a delay
        setTimeout(() => setNotification(null), 3000);
    };


    // DYNAMIC METRICS CALCULATION (useMemo for performance)
    const stats = useMemo(() => {
        const calculatedStats = {
            totalHours: 0,
            completedCount: 0,
            totalEnrolled: courses.length,
            pendingMandatory: 0,
            overallProgress: 0,
            mandatoryCompletedCount: 0,
            electiveCompletedCount: 0,
            inProgressCount: 0,
            toStartCount: 0,
        };

        courses.forEach(c => {
            calculatedStats.totalHours += c.hours;
            
            if (c.progress === 100) {
                calculatedStats.completedCount += 1;
                if (c.type === 'Mandatory') calculatedStats.mandatoryCompletedCount += 1;
                else calculatedStats.electiveCompletedCount += 1;
            } else if (c.progress > 0 && c.progress < 100) {
                calculatedStats.inProgressCount += 1;
            } else if (c.progress === 0) {
                calculatedStats.toStartCount += 1;
            }
            
            if (c.type === 'Mandatory' && c.progress < 100) {
                calculatedStats.pendingMandatory += 1;
            }
            
        });
        
        // Overall progress: Simple percentage of courses completed
        calculatedStats.overallProgress = calculatedStats.totalEnrolled > 0 
            ? Math.round((calculatedStats.completedCount / calculatedStats.totalEnrolled) * 100)
            : 0;
            
        return calculatedStats;
    }, [courses]);


    // FILTERED COURSE LIST (useMemo for performance)
    const filteredCourses = useMemo(() => {
        let list = courses;

        // 1. Filter by Status/Type
        if (filterType === 'inProgress') {
            list = list.filter(c => c.progress > 0 && c.progress < 100);
        } else if (filterType === 'mandatory') {
            list = list.filter(c => c.type === 'Mandatory' && c.progress < 100);
        } else if (filterType === 'completed') {
            list = list.filter(c => c.progress === 100);
        } else if (filterType === 'toStart') {
            list = list.filter(c => c.progress === 0);
        }

        // 2. Filter by Search Term
        if (searchTerm) {
            list = list.filter(c =>
                c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.category.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        return list;
    }, [courses, filterType, searchTerm]);


    const filterOptions = [
        { key: 'inProgress', label: `In Progress (${stats.inProgressCount})` },
        { key: 'mandatory', label: `Mandatory (${stats.pendingMandatory} Pending)` },
        { key: 'toStart', label: `Ready to Start (${stats.toStartCount})` },
        { key: 'completed', label: `Completed (${stats.completedCount})` },
    ];
    
    // UI components for stats and goals remain similar, using dynamic data
    const StatPill = ({ title, value, color }) => (
        <motion.div 
            className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg border border-slate-100 transition duration-300 hover:shadow-xl"
            whileHover={{ y: -2 }}
        >
            <p className={`text-4xl font-extrabold text-${color}-600 font-[Roboto] transition-colors`}>{value}</p>
            <p className="text-sm text-slate-500 font-medium font-[Poppins] mt-1">{title}</p>
        </motion.div>
    );
    
    const GoalCard = ({ goal }) => (
        <div className="p-3 bg-white rounded-lg border border-slate-200 hover:shadow-sm transition duration-200">
            <p className="text-sm font-semibold text-slate-800 font-[Roboto] truncate">{goal.name}</p>
            <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-indigo-500 font-[Poppins] flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" /></svg>
                    {goal.link}
                </p>
                <span className="text-xs font-bold text-slate-700">{goal.progress}%</span>
            </div>
        </div>
    );
    
    const handleNavigationClick = (feature) => {
        setNotification(`Navigating to the ${feature} feature...`);
        setTimeout(() => setNotification(null), 3000);
    };

    return (
        <div className="min-h-screen bg-slate-50 p-4 sm:p-6 font-[Poppins,Roboto,sans-serif]">
            <style>
                {`@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;600&family=Roboto:wght@500;700&display=swap');`}
            </style>
            
            <AnimatePresence>
                {notification && <Notification message={notification} onClose={() => setNotification(null)} />}
            </AnimatePresence>

            <div className="max-w-7xl mx-auto space-y-8">
                
                {/* Header Section */}
                <div className="border-b pb-4">
                    <h1 className="text-3xl font-bold text-slate-900 font-[Roboto]">
                        LMS & Growth Hub
                    </h1>
                    <p className="text-lg text-slate-600 font-[Poppins] mt-1">
                        Personalized learning path for {userData.name}, {userData.role}.
                    </p>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* Left Column (Main Content Area - 2/3 width) */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Quick Stats Pill Panel (Using Dynamic Data) */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <StatPill title="Total Enrolled" value={stats.totalEnrolled} color="indigo" />
                            <StatPill title="Completed Hours" value={stats.completedCount > 0 ? stats.completedCount * 3 : 0} color="emerald" /> {/* Mocking completed hours */}
                            <StatPill title="Pending Mandatory" value={stats.pendingMandatory} color="rose" />
                            <StatPill title="Certificates" value={userData.badgesEarned} color="amber" />
                        </div>
                        
                        {/* Course List & Filtering */}
                        <motion.div 
                            className="bg-white p-6 rounded-2xl shadow-xl border"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <h2 className="text-xl font-bold text-slate-800 mb-4 font-[Roboto]">My Learning Path</h2>

                            {/* Filters and Search Bar */}
                            <div className="flex flex-col md:flex-row gap-4 mb-4">
                                <div className="flex flex-1 overflow-x-auto gap-2 py-1">
                                    {filterOptions.map(option => (
                                        <button
                                            key={option.key}
                                            onClick={() => setFilterType(option.key)}
                                            className={`px-3 py-1 text-sm rounded-full font-medium transition-all flex-shrink-0 ${
                                                filterType === option.key
                                                    ? 'bg-indigo-600 text-white shadow-md'
                                                    : 'bg-slate-100 text-slate-700 hover:bg-indigo-100 hover:text-indigo-700'
                                            } font-[Poppins]`}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                                <div className="relative w-full md:w-auto md:min-w-[200px] flex-shrink-0">
                                    <input
                                        type="text"
                                        placeholder="Search courses..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full p-2 pl-10 border border-slate-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm font-[Poppins]"
                                    />
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-2.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                            </div>

                            {/* Course Cards */}
                            <div className="space-y-4 pt-2 min-h-[300px]">
                                <AnimatePresence mode="wait">
                                    {filteredCourses.length > 0 ? (
                                        filteredCourses.map(course => (
                                            <CourseItemCard key={course.id} course={course} onAction={handleCourseAction} />
                                        ))
                                    ) : (
                                        <motion.div 
                                            key="empty" 
                                            className="text-center p-8 bg-slate-50 rounded-xl text-slate-500"
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                        >
                                            <p className="font-semibold font-[Roboto]">No courses found matching the filter or search term.</p>
                                            <p className="text-sm font-[Poppins]">Try adjusting your filters or browsing the full catalog.</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right Column (Sidebar - 1/3 width) */}
                    <div className="lg:col-span-1 space-y-6">
                        
                        {/* Overall Progress & Analytics (NEW Enterprise Breakdown) */}
                        <motion.div 
                            className="bg-white p-6 rounded-2xl shadow-xl border border-indigo-200"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <h3 className="text-lg font-bold text-slate-800 mb-6 font-[Roboto]">Overall Learning Status</h3>
                            <ProgressStackBreakdown stats={stats} />
                        </motion.div>
                        
                        {/* Goal-Linked Learning Section */}
                        <motion.div 
                            className="bg-white p-6 rounded-2xl shadow-xl border"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: 0.1 }}
                        >
                            <h3 className="text-lg font-bold text-indigo-700 mb-4 font-[Roboto] flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" /></svg>
                                Goals & Recommendations
                            </h3>
                            <p className="text-sm text-slate-600 mb-3 font-[Poppins]">
                                Courses linked directly to your Q1 Performance Goals.
                            </p>
                            <div className="space-y-3">
                                {goalsLinked.map(goal => (
                                    <GoalCard key={goal.id} goal={goal} />
                                ))}
                            </div>
                            <div className="pt-4 text-center">
                                <button 
                                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 font-[Poppins] transition"
                                    onClick={() => handleNavigationClick('Performance Goals Management')}
                                >
                                    Manage Performance Goals
                                </button>
                            </div>
                        </motion.div>

                        {/* Quick Links / Catalog */}
                        <motion.div 
                            className="bg-emerald-50 p-6 rounded-2xl shadow-xl border-t-4 border-emerald-500 flex justify-between items-center"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: 0.2 }}
                        >
                            <div>
                                <h3 className="text-lg font-bold text-emerald-800 font-[Roboto]">
                                    Browse New Skills
                                </h3>
                                <p className="text-sm text-emerald-600 font-[Poppins] mt-1">
                                    Access the full enterprise catalog.
                                </p>
                            </div>
                            <button 
                                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-bold font-[Roboto] transition shadow-lg"
                                onClick={() => handleNavigationClick('Learning Catalog')}
                            >
                                Catalog
                            </button>
                        </motion.div>

                    </div>
                </div>
            </div>
        </div>
    );
}